import React, { useEffect, useState } from 'react'
import { playStreakCelebrationSound } from '../../utils/soundEffects'

interface StreakCelebrationProps {
  isOpen: boolean
  onClose: () => void
  streakCount: number
  isNewStreak?: boolean // First day
}

export function StreakCelebration({
  isOpen,
  onClose,
  streakCount,
  isNewStreak = false
}: StreakCelebrationProps) {
  const [showConfetti, setShowConfetti] = useState(false)
  const [playedSound, setPlayedSound] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true)

      if (!playedSound) {
        playStreakCelebrationSound()
        setPlayedSound(true)
      }

      const timer = setTimeout(() => {
        onClose()
      }, 4000)

      return () => {
        clearTimeout(timer)
        setShowConfetti(false)
      }
    } else {
      setPlayedSound(false)
    }
  }, [isOpen, onClose, playedSound])

  if (!isOpen) return null

  const confettiColors = ['#2BB5A0', '#F4B740', '#F77A9E', '#8B5CF6', '#60A5FA', '#FACC15']
  const confettiCount = 45
  const milestoneMessage = getStreakMilestone(streakCount)
  const encouragement = getStreakMessage(streakCount, isNewStreak)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(confettiCount)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10%',
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                backgroundColor: confettiColors[Math.floor(Math.random() * confettiColors.length)],
                opacity: Math.random() * 0.6 + 0.3,
                transform: `rotate(${Math.random() * 360}deg)`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${Math.random() * 2 + 2}s`
              }}
            />
          ))}
        </div>
      )}

      {/* Celebration Modal */}
      <div className="relative pointer-events-auto w-full max-w-xl px-4">
        <div
          className="relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl shadow-2xl p-8 text-center overflow-hidden animate-scale-in"
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onClose()}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-200/40 via-transparent to-secondary-200/30 pointer-events-none" />
          <div className="relative">
            {/* Toko and badge */}
            <div className="flex flex-col items-center mb-6">
              <div className="text-xs uppercase tracking-[0.3em] text-[var(--color-text-secondary)] mb-3">
                Daily streak
              </div>
              <div className="relative">
                <div className="absolute inset-0 blur-2xl bg-primary-400/30 animate-pulse-soft" />
                <div className="relative w-24 h-24 rounded-full bg-[var(--color-surface-secondary)] border border-[var(--color-border)] shadow-lg flex items-center justify-center">
                  <img
                    src="/frogicon.png"
                    alt="Toko celebrating your streak"
                    className="w-14 h-14"
                    draggable={false}
                  />
                </div>
              </div>
            </div>

            {/* Streak count */}
            <div className="inline-flex items-end justify-center gap-3 bg-[var(--color-surface-secondary)]/80 border border-[var(--color-border)] rounded-2xl px-8 py-5 shadow-md mb-4">
              <span className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
                {streakCount}
              </span>
              <span className="text-2xl font-semibold text-[var(--color-text)]">
                {streakCount === 1 ? 'day' : 'days'}
              </span>
            </div>

            <h2 className="text-3xl font-semibold text-[var(--color-text)] mb-2">
              {isNewStreak ? 'New streak started!' : 'Streak maintained!'}
            </h2>
            <p className="text-lg text-[var(--color-text-secondary)] mb-6">
              {encouragement}
            </p>

            {milestoneMessage && (
              <div className="inline-flex flex-col items-center rounded-full bg-[var(--color-surface-secondary)]/80 border border-[var(--color-border)] px-5 py-2 text-sm font-medium text-[var(--color-text)] mb-5 animate-slide-up">
                <span className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)] mb-0.5">
                  Milestone unlocked
                </span>
                <span>{milestoneMessage}</span>
              </div>
            )}

            <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-xl py-4 px-6 shadow-lg mb-4">
              You're on a {streakCount}-day rhythm with Toko.
            </div>

            <p className="text-sm text-[var(--color-text-secondary)]">
              Tap anywhere to keep the glow going.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function for streak messages
function getStreakMessage(streakCount: number, isNewStreak: boolean): string {
  if (isNewStreak) {
    return "Streak started! Let's build something amazing."
  }

  if (streakCount === 2) return 'Two in a row - momentum unlocked.'
  if (streakCount === 3) return "Three days strong. Toko's proud!"
  if (streakCount === 5) return 'Five days of focus - keep gliding.'
  if (streakCount === 7) return 'One week! That lily pad glow suits you.'
  if (streakCount === 14) return 'Two solid weeks. Consistency looks great on you.'
  if (streakCount === 30) return 'Thirty days! You built a true habit.'
  if (streakCount === 50) return 'Fifty days of dedication - wow.'
  if (streakCount === 100) return 'One hundred days! Legendary energy.'
  if (streakCount >= 365) return 'A full year! Your perseverance shines.'

  return 'Keep that spark alive - Toko is cheering every hop.'
}

// Helper function for milestone badges
function getStreakMilestone(streakCount: number): string | null {
  if (streakCount === 7) return 'One-week glow!'
  if (streakCount === 14) return 'Two-week brilliance!'
  if (streakCount === 30) return '30-day dedication!'
  if (streakCount === 50) return 'Fifty-day focus!'
  if (streakCount === 100) return 'Century club hero!'
  if (streakCount >= 365) return 'One-year mastery!'

  return null
}
